/**
 * AppBootstrapper - Centralized dependency injection and initialization
 * Orchestrates all service initialization in the correct order
 */

import type { AppServices } from '../types/dependencies.types';
import userRepository from '../data/user-repository';
import statsManager from './stats-manager';
import consentManager from '../services/consent';
import flowManager from './flow';
import { Config } from './config';

// Import paywall modules
import { PaddleCheckout } from '../paywall/paddle-checkout';
import { PaywallRenderer } from '../paywall/paywall-renderer';
import { PAYWALL_CONFIG } from '../paywall/paywall-config';
import { PADDLE_PRICES_MAP } from '../paywall/paddle-prices';

// Import decorations
import { SingersCarousel } from '../decorations/singers-carousel';
import { GalleryManager } from '../decorations/gallery';

// Import integrations
import { statsigManager } from '../integrations/statsig';
import { amplitudeManager } from '../integrations/amplitude';
import { metaPixelManager } from '../integrations/meta-pixel';
import { apphudPixelManager } from '../integrations/apphud-pixel';
import { profitWellPixel } from '../integrations/profitwell-pixel';
import { googleTagManager } from '../integrations/google-tag';

// Import voice detection
import voiceDetectionManager from '../voice-detection/voice-detection-manager';
import { getRolloutInfo } from './rollout.ts';
import { BUILD_SHA, BUILD_BRANCH } from './build-info.ts';

// Create singleton instances for payment services
let paddleCheckoutInstance: PaddleCheckout | null = null;
let paywallRendererInstance: PaywallRenderer | null = null;
let singersCarouselInstance: SingersCarousel | null = null;
let galleryManagerInstance: GalleryManager | null = null;

export class AppBootstrapper {
  // Container for initialized services
  private services: AppServices = {};

  // Initialization state
  private initialized: boolean = false;
  private initPromise: Promise<AppServices> | null = null;

  /**
   * Bootstrap the entire application
   */
  async bootstrap(): Promise<AppServices> {
    if (this.initialized) {
      return this.services;
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this._performBootstrap();
    return this.initPromise;
  }

  /**
   * Validate required services exist
   */
  private _validateRequiredServices(): void {
    const missing: string[] = [];
    const available: string[] = [];

    const checks = [
      { name: 'CONFIG', test: () => typeof Config !== 'undefined' },
      { name: 'UserRepository', test: () => typeof userRepository !== 'undefined' },
      { name: 'ConsentManager', test: () => typeof consentManager !== 'undefined' },
      { name: 'StatsManager', test: () => typeof statsManager !== 'undefined' },
      { name: 'FlowManager', test: () => typeof flowManager !== 'undefined' },
    ];

    checks.forEach((check) => {
      if (check.test()) {
        available.push(check.name);
      } else {
        missing.push(check.name);
        console.error(`✗ Missing required service: ${check.name}`);
      }
    });

    if (missing.length > 0) {
      console.error('Available services:', available);
      throw new Error(
        `Missing required services: ${missing.join(', ')}. Check that all scripts are loaded in the correct order.`
      );
    }

    console.log('✓ All required services validated:', available.join(', '));
  }

  /**
   * Determine which paywall to use and store it in userRepository.
   * Priority: URL param (already captured in userRepository.init) > config default > last paywall.
   * Must be called after core services are initialized.
   */
  private _determineSelectedPaywall(): void {
    // Check if paywall was already set from URL param during userRepository.init()
    let paywallId = userRepository.get('paywall');

    if (!paywallId) {
      // Fall back to config default or last paywall
      const paywalls = (PAYWALL_CONFIG as any).paywalls as any[];
      const lastPaywallId = paywalls?.length > 0 ? paywalls[paywalls.length - 1].id : null;
      paywallId = (PAYWALL_CONFIG as any).default_paywall || lastPaywallId || '';

      // Store the resolved paywall ID in userRepository
      if (paywallId) {
        userRepository.set('paywall', paywallId);
      }
    }

    console.log('Selected paywall ID:', paywallId);
  }

  /**
   * Internal bootstrap implementation
   */
  private async _performBootstrap(): Promise<AppServices> {
    console.log('Starting application bootstrap...');
    // QA visibility: which build is actually served + rollout arm (null outside a rollout).
    console.log('Build:', BUILD_SHA, '@', BUILD_BRANCH, '| rollout:', getRolloutInfo());

    // Validate required services exist
    this._validateRequiredServices();

    try {
      // Phase 1: Core services (no dependencies)
      this._initializeCoreServices();

      // Phase 2: Data services (may depend on core)
      this._initializeDataServices();

      // Phase 3: Analytics services (depend on data)
      await this._initializeAnalyticsServices();

      // Phase 4: UI services (depend on data and analytics)
      this._initializeUIServices();

      // Phase 5: Payment services (depend on UI and data)
      await this._initializePaymentServices();

      // Phase 6: Feature services (depend on all above)
      this._initializeFeatureServices();

      // Phase 7: Expose services globally for legacy compatibility
      this._exposeGlobalServices();

      // Final validation
      this._validateInitialization();

      // Phase 8: Start navigation (must happen AFTER payment services are ready)
      // This ensures PaywallRenderer can be initialized when navigating to paywall
      flowManager.startNavigation();

      this.initialized = true;
      console.log('Application bootstrap completed successfully');
      console.log('Initialized services:', Object.keys(this.services));

      return this.services;
    } catch (error) {
      console.error('Bootstrap failed:', error);
      throw error;
    }
  }

  /**
   * Phase 1: Core services
   */
  private _initializeCoreServices(): void {
    console.log('Initializing core services...');

    // UserRepository - no dependencies
    userRepository.init();
    this.services.userRepository = userRepository;

    // StatsManager - centralized event bus (depends on UserRepository)
    statsManager.init({
      userRepository: userRepository,
      config: Config,
    });
    this.services.statsManager = statsManager;

    // ConsentManager - will receive statsig in Phase 3
    this.services.consentManager = consentManager;
  }

  /**
   * Phase 2: Data services
   */
  private _initializeDataServices(): void {
    console.log('Initializing data services...');

    // Alpine store - optional, check if loaded
    if (typeof (window as any).Alpine !== 'undefined') {
      this.services.alpineStore = (window as any).Alpine.store('user');
    }
  }

  /**
   * Phase 3: Analytics services
   */
  private async _initializeAnalyticsServices(): Promise<void> {
    console.log('Initializing analytics services...');

    // Initialize ConsentManager first (other pixels depend on it)
    consentManager.init({ statsManager: this.services.statsManager! });

    // Initialize Statsig (primary analytics)
    try {
      await statsigManager.init({
        userRepository: this.services.userRepository!,
        config: Config,
        statsManager: this.services.statsManager!,
        consentManager: consentManager,
      });
      this.services.statsig = statsigManager;
      console.log('Statsig initialized');
    } catch (error) {
      console.warn('Statsig initialization failed:', error);
      // Continue without Statsig - app should still work
    }

    // Initialize Amplitude (primary product analytics, like Statsig — init immediately, not consent-gated)
    try {
      amplitudeManager.init({
        userRepository: this.services.userRepository!,
        config: Config,
        statsManager: this.services.statsManager!,
        consentManager: consentManager,
      });
      this.services.amplitude = amplitudeManager;
      console.log('Amplitude initialized');
    } catch (error) {
      console.warn('Amplitude initialization failed:', error);
      // Continue without Amplitude - app should still work
    }

    // Initialize Meta Pixel (consent-gated)
    try {
      metaPixelManager.init({
        consentManager: consentManager,
        statsManager: this.services.statsManager!,
        config: Config,
      });
      this.services.metaPixel = metaPixelManager;
      console.log('Meta Pixel manager initialized');
    } catch (error) {
      console.warn('Meta Pixel initialization failed:', error);
    }

    // Initialize Apphud Pixel (consent-gated)
    try {
      apphudPixelManager.init({
        consentManager: consentManager,
        statsManager: this.services.statsManager!,
        config: Config,
      });
      this.services.apphudPixel = apphudPixelManager;
      console.log('Apphud Pixel manager initialized');
    } catch (error) {
      console.warn('Apphud Pixel initialization failed:', error);
    }

    // Initialize ProfitWell Pixel (consent-gated)
    try {
      profitWellPixel.init({
        consentManager: consentManager,
        statsManager: this.services.statsManager!,
        userRepository: this.services.userRepository,
      });
      this.services.profitWellPixel = profitWellPixel;
      console.log('ProfitWell Pixel manager initialized');
    } catch (error) {
      console.warn('ProfitWell Pixel initialization failed:', error);
    }

    // Initialize Google Tag (consent-gated)
    try {
      googleTagManager.init({
        consentManager: consentManager,
        statsManager: this.services.statsManager!,
      });
      this.services.googleTag = googleTagManager;
      console.log('Google Tag manager initialized');
    } catch (error) {
      console.warn('Google Tag initialization failed:', error);
    }
  }

  /**
   * Phase 4: UI services
   */
  private _initializeUIServices(): void {
    console.log('Initializing UI services...');

    // FlowManager - depends on UserRepository, StatsManager, and CONFIG
    const deps = {
      userRepository: this.services.userRepository!,
      statsManager: this.services.statsManager!,
      statsig: this.services.statsig,
      config: Config,
    };

    flowManager.init(deps);
    this.services.flowManager = flowManager;

    // Update StatsManager with FlowManager for screen tracking
    statsManager.updateDependencies({
      flowManager: flowManager,
    });

    // SingersCarousel
    singersCarouselInstance = new SingersCarousel();
    this.services.singersCarousel = singersCarouselInstance;

    // GalleryManager
    galleryManagerInstance = new GalleryManager();
    this.services.galleryManager = galleryManagerInstance;
  }

  /**
   * Phase 5: Payment services
   */
  private async _initializePaymentServices(): Promise<void> {
    console.log('Initializing payment services...');

    // Determine selected paywall early (before price fetching)
    this._determineSelectedPaywall();

    // Create PaddleCheckout instance with config
    paddleCheckoutInstance = new PaddleCheckout({
      config: Config,
      userRepository: this.services.userRepository,
      statsManager: this.services.statsManager,
    });

    // Create PaywallRenderer instance
    paywallRendererInstance = new PaywallRenderer();

    // Store in services
    this.services.paddleCheckout = paddleCheckoutInstance;
    this.services.paywallRenderer = paywallRendererInstance;

    // Initialize Paddle SDK (don't await - let it run in background)
    try {
      await paddleCheckoutInstance.init();
      console.log('Paddle SDK initialized');

      // Fetch prices after Paddle is ready
      const priceBatches = this._getAllPriceIds();
      if (Object.keys(priceBatches).length > 0) {
        await paddleCheckoutInstance.fetchPrices(priceBatches);
        console.log('Prices fetched successfully');
      }
    } catch (error) {
      console.error('Failed to initialize Paddle:', error);
      // Don't throw - app should continue without payment if Paddle fails
    }
  }

  /**
   * Get all price IDs from the selected paywall config grouped by discount
   */
  private _getAllPriceIds(): import('../types/paddle.types').PriceBatches {
    const batches: import('../types/paddle.types').PriceBatches = {};

    // Get paywall ID from userRepository (set by _determineSelectedPaywall)
    const paywallId = userRepository.get('paywall');
    if (!paywallId) {
      console.warn('No paywall ID selected');
      return batches;
    }

    const paywalls = (PAYWALL_CONFIG as any).paywalls as any[];
    const paywall = paywalls?.find((p: any) => p.id === paywallId);

    if (!paywall) {
      console.warn('No paywall config found for:', paywallId);
      return batches;
    }

    // Get the correct environment for price lookup
    const env = Config.environment === 'sandbox' ? 'sandbox' : 'production';
    const pricesMap = (PADDLE_PRICES_MAP as any).environment?.[env] || {};

    // Helper to resolve price ID from PADDLE_PRICES_MAP
    const resolvePaddleId = (priceKey: string): string | null => {
      const entry = pricesMap[priceKey];
      if (typeof entry === 'object' && entry?.paddle_id) {
        return entry.paddle_id;
      }
      return entry || null;
    };

    // Process all prices defined in the paywall
    for (const priceConfig of paywall.prices || []) {
      const discountId = priceConfig.discount_id
        ? resolvePaddleId(priceConfig.discount_id)
        : null;
      const batchKey = discountId || 'no_discount';

      if (!batches[batchKey]) {
        batches[batchKey] = {
          priceIds: [],
          discountId: discountId,
        };
      }

      // Add one-time price ID
      if (priceConfig.one_time_price_id) {
        const paddleId = resolvePaddleId(priceConfig.one_time_price_id);
        if (paddleId && !batches[batchKey].priceIds.includes(paddleId)) {
          batches[batchKey].priceIds.push(paddleId);
        }
      }

      // Add recurring price ID
      if (priceConfig.recurring_price_id) {
        const paddleId = resolvePaddleId(priceConfig.recurring_price_id);
        if (paddleId && !batches[batchKey].priceIds.includes(paddleId)) {
          batches[batchKey].priceIds.push(paddleId);
        }
      }
    }

    console.log('Resolved price batches:', batches);
    return batches;
  }

  /**
   * Phase 6: Feature services
   */
  private _initializeFeatureServices(): void {
    console.log('Initializing feature services...');

    // Voice Detection Manager
    voiceDetectionManager.init({
      userRepository: this.services.userRepository!,
      statsManager: this.services.statsManager!,
    });
    this.services.voiceDetectionManager = voiceDetectionManager;
    console.log('VoiceDetectionManager initialized');
  }

  /**
   * Phase 7: Expose services globally for legacy HTML onclick handlers and flow.ts
   */
  private _exposeGlobalServices(): void {
    console.log('Exposing services globally...');

    const w = window as any;

    // Expose singletons globally for flow.ts compatibility
    w.PaddleCheckout = paddleCheckoutInstance;
    w.PaywallRenderer = paywallRendererInstance;
    w.SingersCarousel = singersCarouselInstance;
    w.GalleryManager = galleryManagerInstance;

    // Expose configs
    w.PAYWALL_CONFIG = PAYWALL_CONFIG;
    w.PADDLE_PRICES_MAP = PADDLE_PRICES_MAP;

    // Expose core services
    w.UserRepository = userRepository;
    w.CONFIG = Config;
    w.FlowManager = flowManager;
    w.StatsManager = statsManager;
    w.ConsentManager = consentManager;

    // Expose voice detection
    w.VoiceDetectionManager = voiceDetectionManager;

    console.log('Services exposed globally');
  }

  /**
   * Helper method to check if a service is available
   */
  hasService(serviceName: string): boolean {
    return !!(this.services as any)[serviceName];
  }

  /**
   * Helper method to get a service
   */
  getService(serviceName: string): any {
    if (!this.initialized) {
      console.warn(`Attempting to get service '${serviceName}' before bootstrap complete`);
    }
    return (this.services as any)[serviceName];
  }

  /**
   * Validate all services were initialized
   */
  private _validateInitialization(): void {
    const missingRequired: string[] = [];
    const errors: string[] = [];

    // Check required services are initialized
    const requiredChecks = ['userRepository', 'consentManager', 'statsManager', 'flowManager'];
    for (const service of requiredChecks) {
      if (!(this.services as any)[service]) {
        missingRequired.push(service);
      }
    }

    // Validate critical dependencies were injected
    if (this.services.flowManager && !(flowManager as any).deps?.userRepository) {
      errors.push('FlowManager missing userRepository dependency');
    }
    if (this.services.statsManager && !(statsManager as any).deps?.config) {
      errors.push('StatsManager missing config dependency');
    }

    // Throw if any critical issues
    if (missingRequired.length > 0) {
      throw new Error(`Failed to initialize required services: ${missingRequired.join(', ')}`);
    }
    if (errors.length > 0) {
      throw new Error(`Dependency injection errors: ${errors.join('; ')}`);
    }

    // Log optional services status
    const optionalStatus: string[] = [];
    for (const service of [
      'alpineStore',
      'singersCarousel',
      'galleryManager',
      'paddleCheckout',
      'paywallRenderer',
    ]) {
      optionalStatus.push(`${service}: ${(this.services as any)[service] ? '✓' : '✗'}`);
    }

    console.log('✓ All required services initialized with dependencies');
    console.log('Optional services:', optionalStatus.join(', '));
  }

  /**
   * Reset for testing
   */
  reset(): void {
    this.services = {};
    this.initialized = false;
    this.initPromise = null;
  }
}

// Create singleton instance
const appBootstrapper = new AppBootstrapper();

export default appBootstrapper;
